import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReadinessRing from "@/components/ReadinessRing";
import { Skeleton } from "@/components/ui/skeleton";
import { Play, Timer, Target, BookOpen, ChevronRight, RotateCcw, Lock, Clock } from "lucide-react";
import { useOffer, formatCountdown } from "@/lib/useOffer";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { offer } = useOffer();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/progress/summary")
      .then((r) => setSummary(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="pt-6 space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-3xl" />
        <Skeleton className="h-64 w-full rounded-3xl" />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="pt-10 text-center">
        <p className="font-bold">Impossibile caricare i dati.</p>
        <button onClick={() => window.location.reload()} className="mt-3 rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)]" data-testid="dashboard-retry-button">
          Riprova
        </button>
      </div>
    );
  }

  const { lastVisited, overall, modules, readiness, weakestBlock, lastExam } = summary;
  const firstName = (user?.nome || "").split(" ")[0];
  const freeUser = Boolean(user && !user.hasFullAccess);  const resumeTarget = lastVisited ? `/lezione/${lastVisited.lessonId}` : modules.length ? `/moduli/${modules[0].id}` : "/moduli";
  const weakLow = weakestBlock && weakestBlock.percent < 60;
  const inProgressModules = modules.filter((m) => m.totalLessons > 0 && m.completedLessons > 0 && m.completedLessons < m.totalLessons);

  return (
    <div className="pt-6 space-y-8">
      {/* Greeting */}
      <div className="fade-up d1">
        <h1 className="text-3xl font-extrabold tracking-tight">Ciao{firstName ? `, ${firstName}` : ""}.</h1>
        <p className="mt-1 text-[color:var(--text-2)] flex items-center gap-2 text-sm">
          Avanzamento corso
          <span className="font-mono font-semibold text-[color:var(--ink)]" data-testid="dashboard-overall-percent">
            {overall.percent}%
          </span>
          <span className="font-mono text-xs">({overall.completedLessons}/{overall.totalLessons} lezioni)</span>
        </p>
      </div>

      {/* Resume card */}
      <section className="rounded-3xl bg-[color:var(--ink)] text-white p-6 shadow-md fade-up d2">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">
          {lastVisited ? "Riprendi da dove eri" : "Inizia il percorso"}
        </p>
        <h2 className="mt-2 text-xl font-extrabold tracking-tight leading-snug">
          {lastVisited ? lastVisited.lessonTitle : "Parti dal Blocco 1: Medicina dello sport."}
        </h2>
        {lastVisited && <p className="mt-1 text-sm text-white/60">{lastVisited.moduleTitle}</p>}
        <button
          onClick={() => navigate(resumeTarget)}
          data-testid="dashboard-resume-study-button"
          className="press mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-6 py-3 min-h-[44px] hover:brightness-95 transition-all duration-200"
        >
          <Play className="w-4 h-4" fill="currentColor" aria-hidden="true" /> {lastVisited ? "Riprendi" : "Inizia"}
        </button>
      </section>

      {/* Free sample simulation — trial users */}
      {freeUser && (
        <section className="rounded-3xl border-2 border-[color:var(--volt)] bg-white p-5 fade-up d2" data-testid="dashboard-sample-card">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--text-2)]">Gratis · Assaggio</p>
              <p className="mt-1 font-extrabold tracking-tight">Simulazione campione</p>
              <p className="mt-1 text-xs text-[color:var(--text-2)] leading-5">20 domande, 5 per blocco: scopri il tuo punto debole.</p>
            </div>
            <button
              onClick={() => navigate("/esame?mode=sample")}
              data-testid="dashboard-start-sample-button"
              className="press shrink-0 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold text-sm px-5 py-2.5 min-h-[44px] hover:brightness-95 transition-all duration-200"
            >
              Prova gratis
            </button>
          </div>
        </section>
      )}

      {/* Unlock full access — only for free users */}
      {freeUser && (
        <Link
          to={offer?.attiva ? "/offerta" : "/sblocca"}
          data-testid="dashboard-unlock-card"
          className="flex items-center justify-between gap-4 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5 hover:bg-white transition-colors duration-200 fade-up d2"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="shrink-0 w-9 h-9 rounded-full bg-[color:var(--volt)] flex items-center justify-center" aria-hidden="true">
              <Lock className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              {offer?.attiva ? (
                <>
                  <p className="font-extrabold text-sm">
                    Offerta early bird:{" "}
                    <span data-testid="dashboard-offer-price">29 €</span>{" "}
                    <span className="text-[color:var(--text-2)] line-through font-mono text-xs">39 €</span>
                  </p>
                  <p className="text-xs text-[color:var(--text-2)] mt-0.5 inline-flex items-center gap-1">
                    {formatCountdown(offer.scade_il) ? (
                      <>
                        <Clock className="w-3 h-3" aria-hidden="true" /> scade tra {formatCountdown(offer.scade_il)} — sblocca tutto l'accesso
                      </>
                    ) : (
                      "Sblocca l'accesso completo a prezzo ridotto"
                    )}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-extrabold text-sm">Sblocca l'accesso completo</p>
                  <p className="text-xs text-[color:var(--text-2)] mt-0.5">5 simulazioni, tutte le lezioni e ripasso errori — 39 € una tantum</p>
                </>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[color:var(--text-2)] shrink-0" />
        </Link>
      )}

      {/* Readiness rings */}
      <section className="fade-up d3">
        <div className="flex items-baseline justify-between mb-1">
          <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">Prontezza per blocco</h2>
          <span className="font-mono text-[11px] text-[color:var(--text-2)]">soglia 9/15</span>
        </div>
        <p className="text-xs text-[color:var(--text-2)] mb-4">Basata sui tuoi quiz e simulazioni recenti.</p>
        <div className="rounded-3xl border border-[color:var(--hairline)] bg-white p-5 shadow-sm">
          <div className="grid grid-cols-2 gap-y-6">
            {readiness.map((r) => (
              <ReadinessRing
                key={r.blocco}
                value={r.percent}
                label={`Blocco ${r.blocco}`}
                sublabel={r.attempts === 0 ? "Nessun quiz ancora" : r.nome}
                testid={`dashboard-block-${r.blocco}-ring`}
              />
            ))}
          </div>
          {weakLow && (
            <div className="mt-5 pt-4 border-t border-[color:var(--hairline)] flex items-center justify-between gap-3" data-testid="dashboard-weakest-block-callout">
              <p className="text-sm leading-5">
                <span className="font-bold">Qui sei sotto il 60%:</span>{" "}
                <span className="text-[color:var(--text-2)]">{weakestBlock.nome} (Blocco {weakestBlock.blocco})</span>
              </p>
              <Link
                to={freeUser ? "/sblocca" : `/esame?mode=block&blocco=${weakestBlock.blocco}`}
                data-testid="dashboard-train-weakest-button"
                className="shrink-0 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
              >
                Allénati
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* How the test works */}
      <section className="rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5 fade-up d4" data-testid="dashboard-test-info-card">
        <p className="text-sm font-extrabold tracking-[0.14em] uppercase">Come funziona il test</p>
        <p className="mt-2 font-mono text-xs">60Q · 60 MIN · 4 BLOCCHI × 15</p>
        <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">
          Servono almeno <strong className="text-[color:var(--ink)]">9/15 in ogni blocco</strong>: il punteggio totale non basta. Gli anelli qui sopra ti dicono quanto sei pronto, blocco per blocco.{" "}
          <Link to="/info" className="underline underline-offset-2 font-semibold text-[color:var(--ink)]" data-testid="dashboard-test-info-link">
            Regole complete
          </Link>
        </p>
      </section>

      {/* Train section */}
      <section className="fade-up d5">
        <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-4">Allénati</h2>
        <div className="space-y-3">
          <Link
            to="/simulazioni"
            data-testid="dashboard-start-exam-button"
            className="block rounded-3xl bg-[color:var(--ink)] text-white p-5 hover:bg-black/90 transition-colors duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-extrabold tracking-tight">Simulazione d'esame</p>
                <p className="font-mono text-xs text-white/60 mt-1">5 ufficiali + casuale · 60Q · 60 min · 9/15 per blocco</p>
              </div>
              <Timer className="w-6 h-6 text-[color:var(--volt)]" />
            </div>
          </Link>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/simulazioni"
              data-testid="dashboard-block-sim-link"
              className="rounded-3xl border border-[color:var(--hairline)] bg-white p-5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
            >
              <Target className="w-5 h-5 mb-2" />
              <p className="font-bold text-sm">Simula un blocco</p>
              <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-1">15Q · 15 min</p>
            </Link>
            <Link
              to="/simulazioni"
              data-testid="dashboard-module-quiz-link"
              className="rounded-3xl border border-[color:var(--hairline)] bg-white p-5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
            >
              <BookOpen className="w-5 h-5 mb-2" />
              <p className="font-bold text-sm">Quiz per modulo</p>
              <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-1">Allenamento mirato</p>
            </Link>
          </div>
          <Link
            to={freeUser ? "/sblocca" : "/ripasso"}
            data-testid="dashboard-review-errors-link"
            className="flex items-center justify-between rounded-3xl border border-[color:var(--hairline)] bg-white p-5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
          >
            <div className="flex items-center gap-3 min-w-0">
              <RotateCcw className="w-5 h-5 shrink-0" />
              <div className="min-w-0">
                <p className="font-bold text-sm">Ripasso errori</p>
                <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5">Rifai le domande che hai sbagliato</p>
              </div>
            </div>
            {freeUser ? (
              <Lock className="w-4 h-4 text-[color:var(--text-2)] shrink-0" aria-hidden="true" />
            ) : summary.reviewErrors > 0 ? (
              <span
                className="shrink-0 font-mono text-xs font-bold rounded-full bg-[color:var(--danger)] text-white px-2.5 py-1"
                data-testid="dashboard-review-errors-count"
                aria-label={`${summary.reviewErrors} domande da ripassare`}
              >
                {summary.reviewErrors}
              </span>
            ) : (
              <ChevronRight className="w-4 h-4 text-[color:var(--text-2)] shrink-0" />
            )}
          </Link>
        </div>
        {lastExam && (
          <p className="mt-3 text-xs text-[color:var(--text-2)]" data-testid="dashboard-last-exam">
            Ultima simulazione completa:{" "}
            <span className={`font-extrabold ${lastExam.esito === "IDONEO" ? "text-[color:var(--ink)]" : "text-[color:var(--danger)]"}`}>{lastExam.esito}</span>{" "}
            <span className="font-mono">({lastExam.totalCorrect}/{lastExam.totalQuestions})</span>
          </p>
        )}
      </section>

      {/* Modules in progress */}
      <section className="fade-up d6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">I tuoi moduli</h2>
          <Link to="/moduli" className="text-xs font-bold underline underline-offset-2" data-testid="dashboard-all-modules-link">
            Vedi tutti
          </Link>
        </div>
        <div className="space-y-2.5">
          {(inProgressModules.length ? inProgressModules : modules.slice(0, 3)).slice(0, 4).map((m) => (
            <Link
              key={m.id}
              to={`/moduli/${m.id}`}
              data-testid={`dashboard-module-${m.id}`}
              className="flex items-center justify-between rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
            >
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{m.titolo}</p>
                <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5">
                  {m.ore}h · BLOCCO {m.blocco} · {m.completedLessons}/{m.totalLessons} lezioni
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-[color:var(--text-2)] shrink-0" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
